import { PlusOutlined } from '@ant-design/icons'
import { App, Button, Divider, Empty, message, Select, Spin } from 'antd'
import { t } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { buildModelFormViewModal } from '../../../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/ModelFormViewModal'
import { type SearchResultRow, useSearchQueue } from '../../../../../recoil/hooks/searchRelation'
import { useApplyMagnifyingGlassChange } from '../../../../../recoil/records/mutations/useApplyMagnifyingGlassChange'
import { viewsContextAtom } from '../../../../../recoil/view/atoms'
import { useUpsertSheetRowMutation } from '../../../../recoil/mutations/upsertSheetRowMutation'
import type { RSheetsCellInputRenderTypeArgument } from '../rSheetsCellInputRenderTypeArgument'

type SearchResultRowWithKey = SearchResultRow & {
  key: string
}

export function RSheetsCellRelationInput({
  parentRecordNode,
  recordNode,
  column,
  rowIndex,
  innerRowIndex,
  defaultWidth,
  finishEditMode,
  boxStyle,
  emptyCell,
  compiledConfig,
  ability,
  query,
}: RSheetsCellInputRenderTypeArgument & {
  emptyCell?: boolean
}) {
  const { modal } = App.useApp()
  const upsertSheetRowMutation = useUpsertSheetRowMutation(recordNode, ability, query)
  const applyMagnifyingGlassChange = useApplyMagnifyingGlassChange(query, ability)
  const reference = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<SearchResultRowWithKey[]>([])
  const { queueSearch, search } = useSearchQueue({
    setLoading,
    setOptions: (options) => {
      setOptions(options.map((x) => ({ ...x, key: x.value })))
    },
  })
  const viewsContext = useRecoilValue(viewsContextAtom)
  const searchSql = column.field?.read.searchSql ?? column.node.read.join?.search.sql ?? ''
  const upsertOrChangeRecord = async (id: string | undefined): Promise<void> => {
    emptyCell === true
      ? await applyMagnifyingGlassChange({
          id: id ?? '',
          rowIndex,
          innerRowIndex,
          node: column.node,
        })
      : await upsertSheetRowMutation({
          value: id ?? '',
          rowIndex,
          innerRowIndex,
          column,
          label: id ?? '', // XXX: mutation側で解決してくれるので、適当に指定している
        })
  }
  useEffect(() => {
    if (options.length === 0) {
      void search(searchSql, '', recordNode ?? parentRecordNode, parentRecordNode) // 空セルのとき、recordNodeは存在せず、parentRecordNodeで検索する
    }

    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (reference.current) {
        reference.current.focus()
      }
    }, 200)
  }, [])

  return (
    <div
      style={{
        ...boxStyle,
        // Spreadsheetと似たロジックで、中身の文字列が増えたら幅を広げて全部表示させる。
        // 他のセルの位置に影響を与えずこれを実現するため、position: absoluteにして実現している
        width: defaultWidth,
        padding: 2,
        backgroundColor: 'white',
        marginTop: 1, // これがないと1pxずれるが、Cell側と比べてなぜ1px分の差があるのか不明
        marginLeft: 1,
        // boxShadow: '1px 4px 7px 2px rgb(60 64 67 / 15%)',
      }}
      onClick={() => {
        finishEditMode()
      }}
    >
      <Select
        popupRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <Button
              disabled={!ability.canOpenRelationInputForm}
              className="w-full"
              type="text"
              icon={<PlusOutlined />}
              onClick={() => {
                const getModelName = () => {
                  if (emptyCell === true) {
                    return {
                      modelName: column.configField?.property.modelName,
                      referencingNode: undefined,
                    }
                  }
                  const referencingNodeName = column.field?.read.labelNodePath?.at(-1)
                  const referencingNode = column.node.children?.find((x) => x.name === referencingNodeName)
                  return {
                    modelName: referencingNode?.write?.streamName,
                    referencingNode,
                  }
                }
                const modelName = getModelName()
                if (modelName.modelName === undefined) {
                  void message.warning(
                    modelName.referencingNode === undefined
                      ? `参照先のモデルが見つかりませんでした。`
                      : `書き込み禁止のオブジェクトのため、新規作成できません。`,
                  )
                  return
                }

                const m = modal.confirm(
                  buildModelFormViewModal({
                    modelName: modelName.modelName,
                    viewAbility: ability,
                    viewsContext,
                    onAfterFinish: (id) => {
                      void upsertOrChangeRecord(id)
                      m.destroy()
                    },
                    onRedirectToSettings: () => {
                      m.destroy()
                    },
                  }),
                )
              }}
            >
              {t('新規作成')}
            </Button>
          </>
        )}
        loading={loading}
        allowClear
        placeholder={t('検索...')}
        autoFocus={true}
        defaultOpen={true}
        variant="borderless"
        // options={options}
        showSearch
        filterOption={false}
        onSearch={(searchKey) => {
          queueSearch(searchSql, searchKey, recordNode ?? parentRecordNode, parentRecordNode) // 空セルのとき、recordNodeは存在せず、parentRecordNodeで検索する
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // 漢字変換中のEnterを区別することができないため、enterのハンドリングはしない。
            // finishEditMode({
            // moveRowDiff: 1, // 2021/11 この場合のエンターは移動させたくないという意見があったため
            // })
            e.preventDefault()
            e.stopPropagation()
          }
          if (e.key === 'Tab') {
            finishEditMode({ moveColumnDiff: 1 })
            e.preventDefault()
            e.stopPropagation()
          }
          if (e.key === 'Escape') {
            finishEditMode()
            e.preventDefault()
            e.stopPropagation()
          }
        }}
        notFoundContent={loading ? <Spin size="small" /> : <Empty />}
        style={{
          width: '100%',
          minWidth: 400,
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onChange={async (value) => {
          const selectedOption = options.find((x) => x.value === value)
          if (selectedOption === undefined) {
            return
          }
          await (emptyCell === true
            ? applyMagnifyingGlassChange({
                id: selectedOption.value,
                rowIndex,
                innerRowIndex,
                node: column.node,
              })
            : upsertSheetRowMutation({
                value: selectedOption.value,
                rowIndex,
                innerRowIndex,
                column,
                label: selectedOption.label,
              }))

          // enterで選択した時はカーソルを下に移動するが、onClickで選択した場合は下に移動させない
          finishEditMode()
        }}
      >
        {options.map((option) => (
          <Select.Option key={option.key} value={option.value}>
            {option.label}
            <span className="ml-2 text-gray-400">
              {option.meta_label === undefined ? '' : '- '}
              {option.meta_label}
            </span>
          </Select.Option>
        ))}
      </Select>
    </div>
  )
}
